import React from "react";
import { Chip } from "@mui/material";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "warning" },
  delivered: { label: "Delivered", color: "success" },
  approved: { label: "Approved", color: "success" },
  /** Moderator approved; not yet final approved. Twin of moderation_rejected. */
  moderation_approved: {
    label: "Approved",
    color: "success",
    variant: "outlined",
  },
  /** Moderator rejected; not yet final rejected (finalized_status empty or not rejected). */
  moderation_rejected: {
    label: "Rejected",
    color: "error",
    variant: "outlined",
  },
  /** Moderator reject only (API status=rejected); Ad Moderation table. */
  rejected: { label: "Rejected", color: "error" },
  /** Final decision rejected (finalized_status=rejected); Ad Deliver + moderation when finalized. */
  final_rejected: { label: "Final rejected", color: "error" },
  /** Final decision approved (finalized_status=approved); Ad Deliver + moderation when finalized. */
  final_approved: { label: "Final approved", color: "success" },
  /** Ad requested, not yet delivered by agency. */
  delivery_pending: { label: "Delivery pending", color: "warning" },
  /** Ad delivered, awaiting Ad Delivery QC review. */
  qc_pending: { label: "QC pending", color: "warning" },
  /** QC asked the agency for changes; awaiting resubmission. */
  qc_changes_requested: {
    label: "QC changes requested",
    color: "error",
    variant: "outlined",
  },
  /** QC approved; entered the moderation queue. */
  qc_approved: { label: "QC approved", color: "success", variant: "outlined" },
  /** QC approved straight to launch, skipping moderation (qc_status=made_live). */
  made_live: { label: "Sent to Launch", color: "success" },
  /** Ad delivered, awaiting moderation decision. */
  moderation_pending: { label: "Moderation pending", color: "warning" },
  /** Final approved, not yet queued for Meta launch. */
  launch_pending: { label: "Launch pending", color: "info" },
  /** Hook approved, but other hooks of the request are not yet (IPD-380). */
  awaiting_hooks: { label: "Waiting on other hooks", color: "default" },
  /** Requester approved an Editing-only rejection (IPD-395). */
  overridden: { label: "Overridden", color: "success" },
  delayed: { label: "Delayed", color: "warning" },
  cancelled: { label: "Cancelled", color: "default" },
  flagged: { label: "Flagged", color: "warning" },
  low: { label: "Low", color: "default" },
  medium: { label: "Medium", color: "info" },
  high: { label: "High", color: "warning" },
  /** AdLaunch lifecycle: queued for launch on Meta. */
  launch_queued: { label: "Queued", color: "default" },
  /** AdLaunch lifecycle: actively launching. */
  launch_processing: { label: "Launching", color: "info" },
  /** AdLaunch lifecycle: live on Meta. */
  launch_success: { label: "Live", color: "success" },
  /** AdLaunch lifecycle: launch failed. */
  launch_failed: { label: "Launch failed", color: "error" },
  /** Auto launch: created on Meta, waiting for its start time. */
  launch_scheduled: { label: "Launch scheduled", color: "info" },
  /** Auto launch: could not be created on Meta. */
  schedule_failed: { label: "Schedule failed", color: "error" },
  /** Launch made by the nightly auto launch, not by hand. */
  auto_launch: { label: "Auto", color: "info", variant: "outlined" },
  /** Live delivery status on Meta (effective_status=ACTIVE). */
  live: { label: "Live", color: "success" },
  /** Paused/archived on Meta (effective_status=PAUSED/ARCHIVED/...). */
  switched_off: { label: "Switch off", color: "default" },
  /** Taxonomy tag: placed in its category. */
  tag_tagged: { label: "Tagged", color: "success" },
  /** Taxonomy tag: the ad belongs to another category. */
  tag_exited: { label: "Exited", color: "info" },
  /** Taxonomy tag: the model's answer broke a rule; a person must correct it. */
  tag_needs_review: { label: "Needs review", color: "error" },
  confidence_high: { label: "High confidence", color: "success" },
  confidence_medium: { label: "Medium confidence", color: "warning" },
  confidence_low: { label: "Low confidence", color: "error" },
  tag_review_unreviewed: { label: "Unreviewed", color: "default" },
  tag_review_approved: { label: "Approved", color: "success" },
  tag_review_corrected: { label: "Corrected", color: "info" },
  /** Meta campaign's own status, on the track campaign mapping. */
  campaign_active: { label: "Active", color: "success" },
  campaign_paused: { label: "Paused", color: "warning" },
  campaign_archived: { label: "Archived", color: "default" },
  campaign_deleted: { label: "Deleted", color: "error" },
  /** Track status of a row in the track campaign mapping upload. */
  track_new: { label: "New track", color: "info" },
  track_updated: { label: "Updates track", color: "warning" },
  track_existing: { label: "Existing", color: "default" },
};

const pillSx = {
  fontWeight: 600,
  textTransform: "capitalize",
  borderRadius: "9999px",
  "&.MuiChip-colorSuccess": {
    bgcolor: "rgba(34, 197, 94, 0.14)",
    color: "#15803d",
  },
  "&.MuiChip-colorWarning": {
    bgcolor: "rgba(234, 179, 8, 0.14)",
    color: "#a16207",
  },
  "&.MuiChip-colorError": {
    bgcolor: "rgba(239, 68, 68, 0.14)",
    color: "#b91c1c",
  },
  "&.MuiChip-colorInfo": {
    bgcolor: "rgba(30, 36, 46, 0.14)",
    color: "#1d4ed8",
  },
  "&.MuiChip-colorDefault": {
    bgcolor: "rgba(107, 114, 128, 0.12)",
    color: "#4b5563",
  },
};

/** Green outline + green label for moderation approved only. */
const approvalOutlinedSx = {
  bgcolor: "transparent",
  borderWidth: "1.5px",
  borderStyle: "solid",
  borderColor: "#16a34a",
  color: "#15803d",
  "&.MuiChip-outlined": {
    bgcolor: "transparent",
    borderColor: "#16a34a",
  },
  "&:hover": {
    bgcolor: "rgba(34, 197, 94, 0.06)",
  },
};

/** Red outline + red label for moderation rejected only. */
const rejectionOutlinedSx = {
  bgcolor: "transparent",
  borderWidth: "1.5px",
  borderStyle: "solid",
  borderColor: "#dc2626",
  color: "#b91c1c",
  "&.MuiChip-outlined": {
    bgcolor: "transparent",
    borderColor: "#dc2626",
  },
  "&:hover": {
    bgcolor: "rgba(239, 68, 68, 0.06)",
  },
};

/**
 * Quieter pill for dense contexts — several chips stacked in one cell, where
 * full-weight ones shout over the row's actual content. Smaller, lighter fill,
 * normal weight instead of semibold. Opt-in so the tables that show a single
 * chip per row keep their current emphasis.
 */
const subtleSx = {
  height: 20,
  fontWeight: 500,
  fontSize: "0.6875rem",
  "& .MuiChip-label": { paddingLeft: "8px", paddingRight: "8px" },
  "&.MuiChip-colorSuccess": {
    bgcolor: "rgba(34, 197, 94, 0.10)",
    color: "#16a34a",
  },
  "&.MuiChip-colorWarning": {
    bgcolor: "rgba(234, 179, 8, 0.12)",
    color: "#b45309",
  },
  "&.MuiChip-colorError": {
    bgcolor: "rgba(239, 68, 68, 0.10)",
    color: "#dc2626",
  },
  "&.MuiChip-colorInfo": {
    bgcolor: "rgba(59, 130, 246, 0.10)",
    color: "#2563eb",
  },
  "&.MuiChip-colorDefault": {
    bgcolor: "rgba(107, 114, 128, 0.08)",
    color: "#6b7280",
  },
  // Outlined variants keep their outline, but a hair thinner and softer.
  "&.MuiChip-outlined": { bgcolor: "transparent", borderWidth: "1px" },
  "&.MuiChip-outlined.MuiChip-colorSuccess": {
    borderColor: "rgba(22, 163, 74, 0.45)",
  },
  "&.MuiChip-outlined.MuiChip-colorError": {
    borderColor: "rgba(220, 38, 38, 0.45)",
  },
};

const StatusChip = ({ status, label, size = "small", subtle = false }) => {
  const config = status ? STATUS_CONFIG[status.toLowerCase()] : null;
  const displayLabel = label ?? config?.label ?? status ?? "—";
  const color = config?.color ?? "default";
  const variant = config?.variant ?? "filled";
  const stLower = (status || "").toLowerCase();
  const isModerationApprovedOutline =
    variant === "outlined" && stLower === "moderation_approved";
  const isModerationRejectedOutline =
    variant === "outlined" && stLower === "moderation_rejected";

  return (
    <Chip
      label={displayLabel}
      color={color}
      variant={variant}
      size={size}
      className="tracking-wider !font-medium"
      sx={[
        pillSx,
        isModerationApprovedOutline ? approvalOutlinedSx : {},
        isModerationRejectedOutline ? rejectionOutlinedSx : {},
        // Last, so it wins over the outline treatments above.
        subtle ? subtleSx : {},
      ]}
    />
  );
};

/** Human label for a status key, for places that need the text without the chip. */
export const statusLabel = (status) =>
  STATUS_CONFIG[String(status || "").toLowerCase()]?.label || status || "";

export default StatusChip;
